'use client'

import type { Vehicle } from '@cruz-com/lookup'
import { Radio, RadioGroup } from '@headlessui/react'
import { useCookieState, useDebounce } from 'ahooks'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Divider, H1, ListBox, LoanItem, NumberFormatInput } from '@/components'
import { toNumber } from '@/lib'
import { defaultLoan, loanDownPaymentOptions, loanRates, loanTerms, pages } from '@/settings'

type Props = {
  initLoan: Vehicle.Loan
}

export const LoanCalculator = ({ initLoan }: Props) => {
  const [loanSettings, setLoanSettings] = useCookieState('loanSettings')
  const [loan, setLoan] = useState<Vehicle.Loan>({
    ...defaultLoan,
    ...initLoan,
    ...JSON.parse(loanSettings || '{}'),
  })
  const [price, setPrice] = useState(loan.price)
  const [tradeIn, setTradeIn] = useState(loan.trade_in)
  const [apr, setAPR] = useState(loan.apr)
  const [shouldChangeApr, setShouldChangeApr] = useState(!loan?.apr)

  function getApr(apr: number | null | undefined, creditScore: string | undefined, condition: string | undefined) {
    if (apr !== null && apr !== undefined) {
      return apr
    }
    const score = (creditScore || 'Great') as keyof typeof loanRates
    const cond = (condition || 'used') as 'new' | 'used'
    return loanRates[score]?.[cond] || 0
  }

  const getCalculatedValues = () => {
    const { price, trade_in, down_payment_percent, term, apr } = loan
    const tPrice = price - (trade_in || 0)
    if (tPrice < 0) {
      return {
        monthlyPayment: 0,
        totalLoanAmount: 0,
        totalInterest: 0,
        description: `$${trade_in?.toLocaleString()} trade-in exceeds vehicle price`,
      }
    }
    const rate = getApr(apr, loan.credit_score, loan.condition)
    const mRate = rate / 12 / 100
    const loanAmount = tPrice - tPrice * (down_payment_percent || 0)
    const safeTerm = term || 1
    const monthlyPayment =
      mRate === 0 ? Math.round(loanAmount / safeTerm) : Math.round((loanAmount * mRate) / (1 - (1 + mRate) ** -safeTerm))
    const totalPaid = monthlyPayment * safeTerm
    const totalInterest = Math.round(totalPaid - loanAmount)
    return {
      monthlyPayment,
      totalLoanAmount: Math.round(loanAmount),
      totalInterest,
      description: `${term} months, ${rate}% APR`,
    }
  }

  const { monthlyPayment, totalLoanAmount, totalInterest, description } = getCalculatedValues()

  // Save loan settings to cookie when they change
  useEffect(() => {
    const loanSettingsString = JSON.stringify(_.pick(loan, ['trade_in', 'down_payment_percent', 'term', 'credit_score', 'apr']))
    setLoanSettings(loanSettingsString)
  }, [loan.trade_in, loan.down_payment_percent, loan.term, loan.credit_score, loan.apr, setLoanSettings])

  // filed changed
  const handleLoanFieldChange = (field: keyof Vehicle.Loan, value: number | string) => {
    if (field === 'condition') setShouldChangeApr(true)
    setLoan((prev: Vehicle.Loan) => ({ ...prev, [field]: value }))
  }

  // price
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(toNumber(e.target.value) || 0)
  }

  const dbPrice = useDebounce(price, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, price: dbPrice }))
  }, [dbPrice])

  // trade-in
  const handleTradeInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTradeIn(toNumber(e.target.value) || 0)
  }

  const dbTradeIn = useDebounce(tradeIn, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, trade_in: dbTradeIn }))
  }, [dbTradeIn])

  // credit score changed
  const handleCreditScoreChange = (val: Vehicle.Loan['credit_score']) => {
    setShouldChangeApr(true)
    setLoan((prev: Vehicle.Loan) => ({ ...prev, credit_score: val }))
  }

  // apr
  const handleAPRChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldChangeApr(false)
    setAPR(toNumber(e.target.value) || 0)
  }

  const dbApr = useDebounce(apr, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, apr: dbApr }))
  }, [dbApr])

  useEffect(() => {
    if (shouldChangeApr) {
      setAPR(getApr(null, loan.credit_score, loan.condition))
    }
  }, [loan.credit_score, loan.condition, shouldChangeApr])

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  const Note = () => (
    <p className='text-sm/6'>
      <span className='font-medium'>Note</span>: The calculator does not include taxes, title fee, registration fee, or insurance.
    </p>
  )

  const Result = (
    <section className='mx-auto max-w-sm space-y-3' aria-live='polite'>
      <h2 className='my-0! py-0! text-center text-base! font-normal!'>Estimated Monthly Payment</h2>
      <div className='font-price pt-2 text-center text-4xl font-semibold'>
        {monthlyPayment >= 0 ? (
          <>
            <sup className='mr-1 text-xl'>$</sup>
            {monthlyPayment?.toLocaleString()}
          </>
        ) : (
          <>-</>
        )}
      </div>
      {description && <div className='text-center text-base font-normal'>{description}</div>}
      <div className='text-sm font-normal'>
        <div className='mt-6 hidden divide-y border-t text-sm md:block'>
          <LoanItem label='Vehicle Price' value={loan?.price ? `$${loan?.price?.toLocaleString()}` : '-'} />
          <LoanItem label='Trade-in Value' value={loan.trade_in ? `- $${loan?.trade_in?.toLocaleString()}` : '-'} />
          <LoanItem
            label='Down Payment'
            value={
              loan?.price && (loan?.down_payment_percent || 0) > 0
                ? `- $${(loan?.price * (loan?.down_payment_percent || 0)).toLocaleString()}`
                : '-'
            }
          />
        </div>
        <div className='divide-y border-t-4 border-zinc-100 dark:border-zinc-700'>
          <LoanItem label='Loan Amount' value={totalLoanAmount ? `$${totalLoanAmount.toLocaleString()}` : '-'} />
          <LoanItem label='Total Interest' value={totalInterest ? `+ $${totalInterest.toLocaleString()}` : '-'} />
          <LoanItem
            label='Total Payment'
            value={totalInterest + totalLoanAmount > 0 ? `$${(totalInterest + totalLoanAmount).toLocaleString()}` : '-'}
            className='font-bold!'
          />
        </div>
      </div>
      <div className='hidden md:mt-6 md:block'>
        <Note />
      </div>
    </section>
  )

  const Form = () => {
    return (
      <>
        <div>
          <label htmlFor='condition' className='form-label'>
            Condition
          </label>
          <RadioGroup
            name='condition'
            value={loan.condition}
            onChange={(val) => handleLoanFieldChange('condition', val)}
            className='radio-group grid w-fit grid-cols-2'
          >
            <Radio value='new' className={twMerge('radio px-4! font-medium!', loan?.condition === 'new' && 'radio-checked')}>
              New
            </Radio>
            <Radio value='used' className={twMerge('radio px-4! font-medium!', loan?.condition === 'used' && 'radio-checked')}>
              Used
            </Radio>
          </RadioGroup>
        </div>
        <div className='grid grid-cols-3 gap-4'>
          <NumberFormatInput
            id='price'
            label='Price'
            prepend='$'
            maxLength={8}
            value={price || 0}
            onFocus={handleInputFocus}
            onChange={handlePriceChange}
          />
          <NumberFormatInput
            id='trade_in'
            label='Trade-in Value'
            prepend='$'
            maxLength={8}
            value={tradeIn || 0}
            onFocus={handleInputFocus}
            onChange={handleTradeInChange}
          />
          <ListBox
            label='Down Payment'
            id='down_payment_percent'
            option={loanDownPaymentOptions}
            value={loan.down_payment_percent}
            onChange={(val) => handleLoanFieldChange('down_payment_percent', _.toNumber(val))}
          />
        </div>
        <div>
          <label htmlFor='term' className='form-label'>
            Loan Term <span className='font-light'>(months)</span>
          </label>
          <RadioGroup
            name='term'
            value={loan.term}
            onChange={(val) => handleLoanFieldChange('term', val)}
            className='radio-group grid w-full grid-cols-6'
          >
            {_.map(loanTerms, (d) => {
              return (
                <Radio key={`term_${d}`} value={d} className={twMerge('radio', loan.term === d && 'radio-checked')}>
                  <div className='space-x-1'>
                    <span className='text-base font-medium'>{d}</span>
                    <span className='block text-xs font-light sm:inline-block'>mo.</span>
                  </div>
                </Radio>
              )
            })}
          </RadioGroup>
        </div>
        <div>
          <label htmlFor='credit_score' className='form-label'>
            Your Credit Score
          </label>
          <RadioGroup
            name='credit_score'
            value={loan.credit_score}
            onChange={(val) => handleCreditScoreChange(val)}
            className='radio-group grid w-full grid-cols-5'
          >
            {_.map(_.keys(loanRates), (key) => {
              const d = _.get(loanRates, key)
              return (
                <Radio key={`cs_${key}`} value={key} className={twMerge('radio', loan.credit_score === key && 'radio-checked')}>
                  <div className='text-sm font-medium md:text-base'>{key}</div>
                  <div className='mt-1 text-center text-xs font-light md:text-sm'>{d.score}</div>
                  <div className='mt-1 text-center text-xs font-light md:text-sm'>
                    APR: <span className='font-semibold'>{_.get(d, loan.condition)}%</span>
                  </div>
                </Radio>
              )
            })}
          </RadioGroup>
        </div>
        <div className='grid grid-cols-3 gap-4'>
          <NumberFormatInput
            id='apr'
            label='APR'
            addon='%'
            maxLength={5}
            value={apr || 0}
            onFocus={handleInputFocus}
            onChange={handleAPRChange}
          />
        </div>
      </>
    )
  }

  return (
    <>
      <H1>{pages.loanCalculator.name}</H1>
      <Divider />
      <p className='mt-6 text-sm/6 font-normal'>
        Use this calculator to estimate your monthly payments on an auto loan.
        <br />
        Adjust the vehicle price, trade-in value, down payment, loan term, and credit score to see how they affect your estimated monthly
        payment.
      </p>
      <section className='mt-6 grid rounded border md:grid-cols-3 md:divide-x md:divide-y-0'>
        <div className='order-2 gap-4 space-y-6 p-4 sm:p-6 md:order-1 md:col-span-2 md:p-8'>{Form()}</div>
        <div className='order-1 w-full grow border-b px-8 pt-6 pb-2 md:order-2 md:border-b-0'>{Result}</div>
      </section>
      <section className='mt-8 space-y-4 text-sm'>
        <div className='md:hidden'>
          <Note />
        </div>
        <p>
          <span className='font-medium'>Disclaimer</span>: This interactive auto loan calculator and all information provided are for
          illustrative purposes only. The accuracy of results and ability to secure financing are not guaranteed.
        </p>
      </section>
    </>
  )
}
